import React, {useCallback, useEffect, useRef, useState} from 'react'
import {findDOMNode} from "react-dom"

import {
    $getSelection,
    $isElementNode,
    $isRangeSelection,
    $isRootOrShadowRoot,
    CAN_REDO_COMMAND,
    CAN_UNDO_COMMAND,
    COMMAND_PRIORITY_CRITICAL,
    ElementFormatType,
    NodeKey,
    SELECTION_CHANGE_COMMAND
} from "lexical"
import {$isHeadingNode} from '@lexical/rich-text'
import {useLexicalComposerContext} from "@lexical/react/LexicalComposerContext"
import {$getSelectionStyleValueForProperty, $isParentElementRTL} from "@lexical/selection"
import {$isListNode, ListNode} from "@lexical/list"
import {$findMatchingParent, $getNearestNodeOfType, mergeRegister} from "@lexical/utils"
import {$isCodeNode, CODE_LANGUAGE_MAP} from "@lexical/code"
import {$isTableNode} from "@lexical/table"
import {getSelectedNode} from "../../utils/getSelectedNode"
import {$isLinkNode} from "@lexical/link"
import HeadingToolbar from "./blocks/heading/heading"
import StylesToolbar from "./blocks/styles/styles"
import HistoryToolbar from "./blocks/history"
import InsertToolbar from "./blocks/insert/insert"
import {BannerType} from "../banner/banner-plugin"
import CombinedToolbar from "./blocks/combined/combined"
import AlignToolbar from "./blocks/align/align"
import ListToolbar from "./blocks/list/list"
import {$isBadgeNode, BadgeType} from "../highlight/badge/badge-plugin"
import {BadgeNode} from "../highlight/badge/node/badge-node"
import {FontColorNode} from "../highlight/font-color/node/font-color-node"
import {$isFontColorNode, FontColorType} from "../highlight/font-color/font-color-plugin"
import MoreToolbar from "./blocks/more/more"
import LexicalEditorLocalSettings from "../../settings/local-settings"
import CodeLanguageToolbar from "./blocks/code-language/code-language"
import AddFieldToolbar from "./blocks/add-field/add-field"
import WriteWithAiToolbar from "./blocks/write-with-ai/write-with-ai";
import {EditorAIOptions} from "../../editor";

export const blockTypeToBlockName = {
    bullet                  : 'Bulleted List',
    check                   : 'Check List',
    code                    : 'Code Block',
    h1                      : 'Heading 1',
    h2                      : 'Heading 2',
    h3                      : 'Heading 3',
    h4                      : 'Heading 4',
    h5                      : 'Heading 5',
    h6                      : 'Heading 6',
    number                  : 'Numbered List',
    'custom-paragraph'      : 'Normal',
    quote                   : 'Quote',
    [BannerType.LIGHT_BLUE] : 'Banner light blue',
    [BannerType.DARK_BLUE]  : 'Banner light blue',
    [BannerType.GREEN]      : 'Banner light blue',
    [BannerType.YELLOW]     : 'Banner light blue',
    [BannerType.ORANGE]     : 'Banner light blue',
    [BannerType.RED]        : 'Banner light blue',
    [BannerType.MAGENTA]    : 'Banner light blue',
    [BannerType.PURPLE]     : 'Banner light blue',
    [BannerType.BROWN]      : 'Banner light blue',
    [BannerType.GRAY]       : 'Banner light blue',
}

const rootTypeToRootName = {
    root        : 'Root',
    table       : 'Table',
}

type Props = {
    localSettings           : LexicalEditorLocalSettings
    addField?               : any                           // used to add "Add field button" used in editor/formulas

    ai?                     : EditorAIOptions,

    controlsParams?         : {
        leftChildren?   : any,
        rightChildren?  : any
    },

    showControls?           : boolean
    showUndoRedo?           : boolean
    showSettingsButton?     : boolean
    hideControlsAllowed?    : boolean
    hidePinOption?          : boolean

    onAddAttachment?        : {(type: 'local' | 'google-drive'):void}
}

const debug = false
// const debug = "ToolBarPlugin"

export default function ToolBarPlugin(props:Props): JSX.Element {
    const elementRef: any                               = useRef()

    const [editor]                                      = useLexicalComposerContext()

    const [activeEditor, setActiveEditor]               = useState(editor)
    const [blockType, setBlockType]                     = useState<keyof typeof blockTypeToBlockName>('custom-paragraph')
    const [badgeType, setBadgeType]                     = useState<BadgeType | null>(null)
    const [fontColorType, setFontColorType]             = useState<FontColorType | null>(null)
    const [rootType, setRootType]                       = useState<keyof typeof rootTypeToRootName>('root')
    const [selectedElementKey, setSelectedElementKey]   = useState<NodeKey | null>(null)
    const [fontSize, setFontSize]                       = useState<string>('15px')
    const [fontColor, setFontColor]                     = useState<string>('#000')
    const [bgColor, setBgColor]                         = useState<string>('unset')
    const [fontFamily, setFontFamily]                   = useState<string>('Arial')
    const [elementFormat, setElementFormat]             = useState<ElementFormatType>('left')
    const [isLink, setIsLink]                           = useState(false)
    const [isBold, setIsBold]                           = useState(false)
    const [isItalic, setIsItalic]                       = useState(false)
    const [isUnderline, setIsUnderline]                 = useState(false)
    const [isStrikethrough, setIsStrikethrough]         = useState(false)
    const [isSubscript, setIsSubscript]                 = useState(false)
    const [isSuperscript, setIsSuperscript]             = useState(false)
    const [isCode, setIsCode]                           = useState(false)
    const [canUndo, setCanUndo]                         = useState(false)
    const [canRedo, setCanRedo]                         = useState(false)
    // const [modal, showModal]                            = useModal()
    const [isRTL, setIsRTL]                             = useState(false)
    const [codeLanguage, setCodeLanguage]               = useState<string>('')
    const [isEditable, setIsEditable]                   = useState(() => editor.isEditable())

    const $updateToolbar = useCallback(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
            const anchorNode = selection.anchor.getNode();
            let element =
                anchorNode.getKey() === 'root'
                    ? anchorNode
                    : $findMatchingParent(anchorNode, (e) => {
                        const parent = e.getParent()
                        return parent !== null && $isRootOrShadowRoot(parent);
                    });

            if (element === null) {
                element = anchorNode.getTopLevelElementOrThrow()
            }

            const elementKey = element.getKey()
            const elementDOM = activeEditor.getElementByKey(elementKey)

            // Update text format
            setIsBold(selection.hasFormat('bold'))
            setIsItalic(selection.hasFormat('italic'))
            setIsUnderline(selection.hasFormat('underline'))
            setIsStrikethrough(selection.hasFormat('strikethrough'))
            setIsSubscript(selection.hasFormat('subscript'))
            setIsSuperscript(selection.hasFormat('superscript'))
            setIsCode(selection.hasFormat('code'))
            setIsRTL($isParentElementRTL(selection))

            // Update links
            const node = getSelectedNode(selection);
            const parent = node.getParent();
            if ($isLinkNode(parent) || $isLinkNode(node)) {
                setIsLink(true);
            } else {
                setIsLink(false);
            }

            //update badges
            let badgeNode: BadgeNode | undefined
            if ($isBadgeNode(node))                 badgeNode = node
            if (!badgeNode && $isBadgeNode(parent)) badgeNode = parent
            if (!!badgeNode) {
                setBadgeType(badgeNode.getType())
            } else {
                setBadgeType(null)
            }

            //update font color
            let fontColorNode: FontColorNode | undefined
            if ($isFontColorNode(node))                     fontColorNode = node
            if (!fontColorNode && $isFontColorNode(parent)) fontColorNode = parent
            if (!!fontColorNode) {
                setFontColorType(fontColorNode.getType())
            } else {
                setFontColorType(null)
            }

            const tableNode = $findMatchingParent(node, $isTableNode);
            if ($isTableNode(tableNode)) {
                setRootType('table');
            } else {
                setRootType('root');
            }

            if (elementDOM !== null) {
                setSelectedElementKey(elementKey);
                if ($isListNode(element)) {
                    const parentList = $getNearestNodeOfType<ListNode>(
                        anchorNode,
                        ListNode,
                    );
                    const type = parentList
                        ? parentList.getListType()
                        : element.getListType();
                    setBlockType(type);
                } else {
                    const type = $isHeadingNode(element)
                        ? element.getTag()
                        : element.getType();
                    if (type in blockTypeToBlockName) {
                        setBlockType(type as keyof typeof blockTypeToBlockName);
                    }
                    if ($isCodeNode(element)) {
                        const language =
                            element.getLanguage() as keyof typeof CODE_LANGUAGE_MAP;
                        setCodeLanguage(
                            language ? CODE_LANGUAGE_MAP[language] || language : '',
                        );
                        return;
                    }
                }
            }
            // Handle buttons
            setFontSize(
                $getSelectionStyleValueForProperty(selection, 'font-size', '15px'),
            );
            setFontColor(
                $getSelectionStyleValueForProperty(selection, 'color', '#000'),
            );
            setBgColor(
                $getSelectionStyleValueForProperty(
                    selection,
                    'background-color',
                    'unset',
                ),
            );
            setFontFamily(
                $getSelectionStyleValueForProperty(selection, 'font-family', 'Arial'),
            );
            setElementFormat(
                ($isElementNode(node)
                    ? node.getFormatType()
                    : parent?.getFormatType()) || 'left',
            );
        }
    }, [activeEditor]);

    useEffect(() => {
        return editor.registerCommand(
            SELECTION_CHANGE_COMMAND,
            (_payload, newEditor) => {
                $updateToolbar();
                setActiveEditor(newEditor);
                return false;
            },
            COMMAND_PRIORITY_CRITICAL,
        );
    }, [editor, $updateToolbar]);

    useEffect(() => {
        return mergeRegister(
            editor.registerEditableListener((editable) => {
                setIsEditable(editable);
            }),
            activeEditor.registerUpdateListener(({editorState}) => {
                editorState.read(() => {
                    $updateToolbar();
                }, {editor: activeEditor});
            }),
            activeEditor.registerCommand<boolean>(
                CAN_UNDO_COMMAND,
                (payload) => {
                    setCanUndo(payload);
                    return false;
                },
                COMMAND_PRIORITY_CRITICAL,
            ),
            activeEditor.registerCommand<boolean>(
                CAN_REDO_COMMAND,
                (payload) => {
                    setCanRedo(payload);
                    return false;
                },
                COMMAND_PRIORITY_CRITICAL,
            ),
        )
    }, [$updateToolbar, activeEditor, editor]);

    useEffect(() => {
        if (!elementRef.current) return
        const node:any = findDOMNode(elementRef.current)
        const resizeObserver = new ResizeObserver(() => {
            debug && console.log(debug, 'ResizeObserver', node.offsetWidth)
            gd.bus.triggerEvent('lexical-toolbar:width-changed', node.offsetWidth)
        })
        resizeObserver.observe(node)
        return () => resizeObserver.disconnect() // clean up
    }, [elementRef.current])

    debug && console.log(debug, 'render', bgColor)

    const leftChildren  = _.get(props, 'controlsParams.leftChildren', false)
    const rightChildren = _.get(props, 'controlsParams.rightChildren', false)

    const classes = Classes("gd-draftail-toolbar lexical-toolbar", {
        'is-pinned'     : props.localSettings.pinToolBar,
        'no-controls'   : !props.showControls
    })

    switch (blockType) {
        case 'code':
            return (
                <div ref={elementRef} className={classes}>

                    {props.showControls &&
                        <>
                            {leftChildren}

                            <HeadingToolbar editor={activeEditor}
                                            blockType={blockType}/>

                            <div className="rte-toolbar-separator"/>

                            <CodeLanguageToolbar editor={activeEditor}
                                                 selectedElementKey={selectedElementKey}
                                                 codeLanguage={codeLanguage}/>

                            <div className="rte-toolbar-separator"/>

                            <AlignToolbar editor={activeEditor}
                                          elementFormat={elementFormat}
                                          isRTL={isRTL}
                                          blockType={blockType}/>

                            <div className="rte-toolbar-separator"/>

                            <HistoryToolbar editor={activeEditor}
                                            canUndo={canUndo}
                                            canRedo={canRedo}/>

                            {rightChildren}
                        </>}

                    {props.showSettingsButton &&
                        <MoreToolbar editor                 = {activeEditor}
                                     hideControlsAllowed    = {props.hideControlsAllowed}
                                     hidePinOption          = {props.hidePinOption}
                                     localSettings          = {props.localSettings}/>}
                </div>
            )

        default:
            return (
                <div ref={elementRef} className={classes}>

                    {props.showControls &&
                        <>
                            {leftChildren}

                            <AddFieldToolbar addField={props.addField}/>

                            <HeadingToolbar editor={activeEditor}
                                            blockType={blockType}/>


                            {gd.company.acl.canWriteWithAI &&
                                <>
                                    <div className="rte-toolbar-separator"/>
                                    <WriteWithAiToolbar
                                        editor  = {activeEditor}
                                        ai      = {props.ai}
                                    />
                                </>
                            }

                            <div className="rte-toolbar-separator"/>

                            <StylesToolbar editor={activeEditor}
                                           isBold={isBold}
                                           isItalic={isItalic}
                                           isUnderline={isUnderline}
                                           isCode={isCode}
                                           isStrikethrough={isStrikethrough}
                                           isSubscript={isSubscript}
                                           isSuperscript={isSuperscript}
                                           fontColorType={fontColorType}
                                           badgeType={badgeType}
                            />

                            <div className="rte-toolbar-separator"/>

                            <CombinedToolbar editor             = {activeEditor}
                                             fontColorType      = {fontColorType}
                                             badgeType          = {badgeType}
                                             onAddAttachment    = {props.onAddAttachment}/>

                            <div className="rte-toolbar-separator"/>

                            <AlignToolbar editor={activeEditor}
                                          elementFormat={elementFormat}
                                          isRTL={isRTL}
                                          blockType={blockType}/>

                            <ListToolbar editor={activeEditor}
                                         blockType={blockType}/>

                            <div className="rte-toolbar-separator"/>

                            {/*<ListToolbar editor     = {activeEditor}*/}
                            {/*             blockType  = {blockType}/>*/}

                            <InsertToolbar editor={activeEditor}/>

                            {props.showUndoRedo &&
                                <>
                                    <div className="rte-toolbar-separator"/>
                                    <HistoryToolbar editor={activeEditor}
                                                    canUndo={canUndo}
                                                    canRedo={canRedo}/>
                                </>}

                            {rightChildren}
                        </>}

                    {props.showSettingsButton &&
                        <MoreToolbar editor                 = {activeEditor}
                                     hideControlsAllowed    = {props.hideControlsAllowed}
                                     hidePinOption          = {props.hidePinOption}
                                     localSettings          = {props.localSettings}/>}
                </div>
            )
    }
}